import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiX, FiZap, FiTrendingUp } from 'react-icons/fi';
import Cropper from 'react-easy-crop';
import { useProducts } from '../../hooks/useProducts';
import { useCategories } from '../../hooks/useCategories';
import { useBrands } from '../../hooks/useBrands';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { supabase } from '../../config/supabase';
import { fetchProductSalesStatsBatched, fetchReviewsDataBatched } from '../../utils/productSorting';

const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.variant === 'primary' ? '#5efba9' : 'white'};
  color: ${props => props.variant === 'primary' ? '#1f2937' : '#6b7280'};
  border: ${props => props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#4de89e' : '#f9fafb'};
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const AddImageButton = styled.button`
  padding: 8px 16px;
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #86efac;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #dcfce7;
  }
`;

const ImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const ImagePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
`;

const ImagePreviewImg = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
`;

const RemoveImageButton = styled.button`
  padding: 8px 16px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #fecaca;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #5efba9;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Custom Confirmation Dialog Styles
const ConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 1001;
  max-width: 450px;
  width: 90%;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 28px;
  background: ${props => props.type === 'warning' ? '#FEF3C7' : props.type === 'success' ? '#D1FAE5' : '#DBEAFE'};
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  text-align: center;
`;

const ConfirmMessage = styled.p`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 24px 0;
  text-align: center;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 12px 28px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.primary ? `
    background: #5efba9;
    color: #1f2937;
    &:hover {
      background: #4de89e;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(94, 251, 169, 0.4);
    }
  ` : `
    background: white;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    &:hover {
      background: #f9fafb;
      border-color: #d1d5db;
    }
  `}
`;

// Toast Notification Styles
const ToastContainer = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
`;

const Toast = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-left: 4px solid ${props => 
    props.type === 'success' ? '#10b981' : 
    props.type === 'error' ? '#ef4444' : 
    props.type === 'warning' ? '#f59e0b' : '#3b82f6'};
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ToastIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
`;

const ToastTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  margin-bottom: 4px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  
  &:hover {
    color: #4b5563;
  }
`;

// Table Styles
const TableWrapper = styled.div`
  ${scrollableTableWrapStyles}
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: ${(props) => {
    if (props.$rank === 1) return 'inset 4px 0 0 #eab308';
    if (props.$rank === 2) return 'inset 4px 0 0 #94a3b8';
    if (props.$rank === 3) return 'inset 4px 0 0 #ea580c';
    return 'none';
  }};
  background: ${(props) => {
    if (props.$boosted) {
      return 'linear-gradient(90deg, #fffbeb 0%, #fef3c7 50%, #fffbeb 100%)';
    }
    if (props.$rank === 1) {
      return 'linear-gradient(90deg, rgba(254, 243, 199, 0.55) 0%, rgba(255, 255, 255, 0.9) 48%)';
    }
    if (props.$rank === 2) {
      return 'linear-gradient(90deg, rgba(226, 232, 240, 0.55) 0%, rgba(255, 255, 255, 0.9) 48%)';
    }
    if (props.$rank === 3) {
      return 'linear-gradient(90deg, rgba(254, 215, 170, 0.45) 0%, rgba(255, 255, 255, 0.9) 48%)';
    }
    return 'transparent';
  }};

  &:hover {
    background: ${(props) => {
      if (props.$boosted) {
        return 'linear-gradient(90deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)';
      }
      if (props.$rank && props.$rank <= 3) {
        return 'linear-gradient(90deg, rgba(240, 253, 244, 0.9) 0%, #f9fafb 100%)';
      }
      return '#f9fafb';
    }};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
  vertical-align: middle;
`;

const SortHintBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfeff 55%, #f5f3ff 100%);
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  font-size: 13px;
  color: #14532d;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  svg {
    flex-shrink: 0;
    color: #059669;
  }

  strong {
    color: #166534;
  }
`;

const RankBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tier === 1 ? '#92400e' : p.$tier === 2 ? '#475569' : p.$tier === 3 ? '#9a3412' : '#6b7280'};
  background: ${(p) =>
    p.$tier === 1
      ? 'linear-gradient(145deg, #fde68a, #fcd34d)'
      : p.$tier === 2
        ? 'linear-gradient(145deg, #e2e8f0, #cbd5e1)'
        : p.$tier === 3
          ? 'linear-gradient(145deg, #fdba74, #fb923c)'
          : '#f3f4f6'};
  border: 1px solid
    ${(p) =>
      p.$tier === 1 ? '#fbbf24' : p.$tier === 2 ? '#cbd5e1' : p.$tier === 3 ? '#fdba74' : '#e5e7eb'};
  box-shadow: ${(p) => (p.$tier && p.$tier <= 3 ? '0 1px 2px rgba(0,0,0,0.06)' : 'none')};
`;

const UnitsPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
`;

const ReviewsPill = styled(UnitsPill)`
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
`;

const RevenueText = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  letter-spacing: 0.02em;
`;

const ProductImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
`;

const ImagePlaceholder = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.status === 'active' ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.status === 'active' ? '#166534' : '#92400e'};
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const BoostToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 2px solid ${props => props.$boosted ? '#f59e0b' : '#e5e7eb'};
  background: ${props => props.$boosted ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'white'};
  color: ${props => props.$boosted ? '#d97706' : '#9ca3af'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #f59e0b;
    background: ${props => props.$boosted ? 'linear-gradient(135deg, #fde68a 0%, #fcd34d 100%)' : '#fffbeb'};
    color: #d97706;
    transform: scale(1.05);
  }
  
  svg {
    font-size: 18px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;
  max-width: 460px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const BrandFilterSelect = styled.select`
  min-width: 220px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

// Variant Modal Styled Components
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

export default function AdminProducts() {
  const { products: dbProducts, loading } = useProducts();
  const { categories } = useCategories();
  const { brands } = useBrands();
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();

  // LocalStorage key for draft product data
  const DRAFT_PRODUCT_KEY = 'superadmin_product_draft';

  const [products, setProducts] = useState([]);
  // Never auto-open product modal on page entry.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    sku: '',
    description: '',
    images: [],
    price: 0,
    currency: 'OMR',
    stock: 0,
    weight_kg: 1.000,
    status: 'active',
    brand_id: '',
    category_id: '',
    subcategory_id: '',
    attributes: {},
    published_at: null,
    dis: 'no',
  });
  // const [newImageUrl, setNewImageUrl] = useState('');
  const additionalImagesFileInputRef = React.createRef();
  const [uploadingAdditionalImages, setUploadingAdditionalImages] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState('additional');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = React.createRef();

  // Notification system state
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);

  // Toast functions
  const showToast = (title, message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, title, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Confirm dialog function
  const showConfirm = (title, message, icon, type = 'warning', cancelLabel = null, confirmLabel = null) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        title,
        message,
        icon,
        type,
        cancelLabel: cancelLabel || 'Cancel',
        confirmLabel: confirmLabel || 'Confirm',
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        }
      });
    });
  };

  const [submitting, setSubmitting] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');

  // Variant management state
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [optionGroups, setOptionGroups] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  // Option group editing state
  const [editingOptionGroup, setEditingOptionGroup] = useState(null);
  const [isAddingOptionGroup, setIsAddingOptionGroup] = useState(false);
  const [optionGroupForm, setOptionGroupForm] = useState({ name: '', label: '', position: 0 });
  const [displayedCount, setDisplayedCount] = useState(20);
  const [salesStats, setSalesStats] = useState({});
  const [salesStatsLoading, setSalesStatsLoading] = useState(false);
  const [reviewsStats, setReviewsStats] = useState({});
  const [reviewsStatsLoading, setReviewsStatsLoading] = useState(false);

  // Option value editing state  
  const [editingOptionValue, setEditingOptionValue] = useState(null);
  const [optionValueForm, setOptionValueForm] = useState({ value: '', position: 0, group_id: '' });

  // Variant editing state
  const [editingVariant, setEditingVariant] = useState(null);
  const [isAddingVariant, setIsAddingVariant] = useState(false);
  const [uploadingVariantImage, setUploadingVariantImage] = useState(false);
  const variantImageInputRef = React.createRef();
  const [variantForm, setVariantForm] = useState({
    sku: '',
    price: 0,
    stock: 0,
    status: 'active',
    img: '',
    images: [],
    attributes: {},
    selectedOptions: {} // { groupId: valueId }
  });
  const PRODUCT_IMAGE_WIDTH = 1200;
  const PRODUCT_IMAGE_HEIGHT = 1200;
  const PRODUCT_IMAGE_ASPECT = 1;

  useEffect(() => {
    if (dbProducts) {
      setProducts(dbProducts);
    }
  }, [dbProducts]);

  useEffect(() => {
    const ids = products.map((p) => p.id).filter(Boolean);
    if (ids.length === 0) {
      setSalesStats({});
      return;
    }
    let cancelled = false;
    setSalesStatsLoading(true);
    fetchProductSalesStatsBatched(ids)
      .then((merged) => {
        if (!cancelled) setSalesStats(merged);
      })
      .catch(() => {
        if (!cancelled) setSalesStats({});
      })
      .finally(() => {
        if (!cancelled) setSalesStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [products]);

  useEffect(() => {
    const ids = products.map((p) => p.id).filter(Boolean);
    if (ids.length === 0) {
      setReviewsStats({});
      return;
    }
    let cancelled = false;
    setReviewsStatsLoading(true);
    fetchReviewsDataBatched(ids)
      .then((merged) => {
        if (!cancelled) setReviewsStats(merged);
      })
      .catch(() => {
        if (!cancelled) setReviewsStats({});
      })
      .finally(() => {
        if (!cancelled) setReviewsStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [products]);

  // Manual refresh function to refetch products
  const refreshProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, name, name_ar, slug, sku, description, img, images, attributes, price, currency, stock, status, created_at, updated_at, brand_id, dis, boost,
          category_id, category:categories(name_en, name_ar),
          subcategory_id, subcategory:subcategories(name_en, name_ar),
          brand:brands!inner(id, name, is_active, phone)
        `)
        .eq("brands.is_active", true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data) {
        setProducts(data);
      }
    } catch (err) {
      console.error('Error refreshing products:', err);
    }
  };

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (formData.category_id) {
        try {
          console.log('Fetching subcategories for category:', formData.category_id);
          const { data, error } = await supabase
            .from('subcategories')
            .select('id, name_en, name_ar, category_id')
            .eq('category_id', formData.category_id)
            .order('name_en', { ascending: true });

          console.log('Subcategories response:', { data, error });

          if (error) {
            console.error('Error fetching subcategories:', error);
            // Don't show alert for edit mode
            if (!editingProduct) {
              alert('Error fetching subcategories: ' + error.message);
            }
            setSubcategories([]);
          } else {
            console.log(`Found ${data?.length || 0} subcategories`);
            setSubcategories(data || []);
          }
        } catch (err) {
          console.error('Error fetching subcategories:', err);
          setSubcategories([]);
        }
      } else {
        console.log('No category selected, clearing subcategories');
        setSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [formData.category_id, editingProduct]);

  // Detect draft presence only; restoration stays manual when user clicks Add Product.
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_PRODUCT_KEY);
      if (savedDraft) {
        setHasDraft(true);
      }
    } catch (error) {
      console.error('Failed to read draft on mount:', error);
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save form data to localStorage (only for new products, not when editing)
  // Debounced to prevent multiple rapid saves
  useEffect(() => {
    if (isModalOpen && !editingProduct) {
      // Check if form has meaningful data before saving
      const hasData = formData.name || formData.name_ar || formData.sku || 
                      formData.description || formData.img || 
                      formData.images.length > 0 || formData.price > 0;
      
      if (hasData) {
        // Debounce the save to prevent multiple rapid saves
        const saveTimeout = setTimeout(() => {
          try {
          // Ensure status is always 'active' or 'inactive' for drafts
            const draftData = {
              ...formData,
            status: (formData.status === 'active' || formData.status === 'inactive') 
                ? formData.status 
              : 'active', // Default to 'active' if status is invalid
              savedAt: new Date().toISOString(),
              modalWasOpen: true
            };
            localStorage.setItem(DRAFT_PRODUCT_KEY, JSON.stringify(draftData));
            console.log('📝 Draft saved to localStorage');
          } catch (error) {
            console.error('Failed to save draft:', error);
          }
        }, 500); // 500ms debounce

        return () => clearTimeout(saveTimeout);
      }
    }
  }, [formData, isModalOpen, editingProduct, DRAFT_PRODUCT_KEY]);

  // Prevent accidental page reload/close when there's unsaved data
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isModalOpen && !editingProduct) {
        const hasData = formData.name || formData.name_ar || formData.sku || 
                        formData.description || formData.img || 
                        formData.images.length > 0 || formData.price > 0;
        
        if (hasData) {
          // Save one final time before potentially leaving
          try {
            // Ensure status is always 'active' or 'inactive' for drafts
            const draftData = {
              ...formData,
              status: (formData.status === 'active' || formData.status === 'inactive') 
                ? formData.status 
                : 'active', // Default to 'active' if status is invalid
              savedAt: new Date().toISOString(),
              modalWasOpen: true
            };
            localStorage.setItem(DRAFT_PRODUCT_KEY, JSON.stringify(draftData));
          } catch (error) {
            console.error('Failed to save draft on unload:', error);
          }
          
          // Show browser confirmation dialog
          e.preventDefault();
          e.returnValue = '';
          return '';
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isModalOpen, editingProduct, formData, DRAFT_PRODUCT_KEY]);

  // Save draft when user switches tabs (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isModalOpen && !editingProduct) {
        const hasData = formData.name || formData.name_ar || formData.sku || 
                        formData.description || formData.img || 
                        formData.images.length > 0 || formData.price > 0;
        
        if (hasData) {
          try {
            localStorage.setItem(DRAFT_PRODUCT_KEY, JSON.stringify({
              ...formData,
              savedAt: new Date().toISOString(),
              modalWasOpen: true
            }));
            console.log('💾 Draft saved on tab switch');
          } catch (error) {
            console.error('Failed to save draft on visibility change:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isModalOpen, editingProduct, formData, DRAFT_PRODUCT_KEY]);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (src, pixelCrop) => {
    const image = await createImage(src);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');
    canvas.width = PRODUCT_IMAGE_WIDTH;
    canvas.height = PRODUCT_IMAGE_HEIGHT;
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      PRODUCT_IMAGE_WIDTH,
      PRODUCT_IMAGE_HEIGHT
    );
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
    });
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleAddImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropTarget('additional');
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    if (additionalImagesFileInputRef.current) {
      additionalImagesFileInputRef.current.value = '';
    }
  };

  const handleMainImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropTarget('main');
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setUploadingAdditionalImages(true);
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (!croppedImage) throw new Error('Failed to crop image');
      const fileName = `product_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(fileName, croppedImage, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg',
        });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('products')
        .getPublicUrl(fileName);

      setFormData((prev) => {
        if (cropTarget === 'main') {
          return {
            ...prev,
            img: publicUrl,
          };
        }
        return {
          ...prev,
          images: [...(prev.images || []), publicUrl],
        };
      });

      setShowCropModal(false);
      setImageSrc(null);
      setCroppedAreaPixels(null);
      showToast(
        isArabic ? '✅ تم رفع الصورة' : '✅ Image Uploaded',
        isArabic ? 'تم قص الصورة ورفعها بنجاح' : 'Image was cropped and uploaded successfully.',
        'success'
      );
    } catch (err) {
      console.error('Error cropping/uploading image:', err);
      showToast(
        isArabic ? '❌ فشل المعالجة' : '❌ Upload Failed',
        (isArabic ? 'تعذر قص/رفع الصورة: ' : 'Could not crop/upload image: ') + err.message,
        'error'
      );
    } finally {
      setUploadingAdditionalImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      // If category changes, reset subcategory
      if (name === 'category_id') {
        return {
          ...prev,
          category_id: value,
          subcategory_id: '' // Reset subcategory when category changes
        };
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleEdit = (product) => {
    console.log('Editing product:', product);
    setEditingProduct(product);

    // Normalize status to active/inactive (Admin & Super Admin use the same)
    const validStatus =
      product.status === 'active' || product.status === 'inactive'
        ? product.status
        : (product.status === 'published' ? 'active' : 'inactive');

    setFormData({
      name: product.name || '',
      name_ar: product.name_ar || '',
      sku: product.sku || '',
      description: product.description || '',
      img: product.img || '',
      images: product.images || [],
      price: product.price || 0,
      currency: product.currency || 'OMR',
      stock: product.stock || 0,
      weight_kg: product.weight_kg ?? 1.000,
      status: validStatus,
      brand_id: product.brand_id || '',
      category_id: product.category_id || '',
      subcategory_id: product.subcategory_id || '',
      attributes: product.attributes || {},
      published_at: product.published_at || null,
      dis: product.dis || 'no',
    });
    console.log('Form data set:', {
      brand_id: product.brand_id,
      category_id: product.category_id,
      subcategory_id: product.subcategory_id,
      dis: product.dis || 'no'
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (productId) => {
    try {
      // First, check if product is linked to any orders to show an explicit warning.
      const { data: orderItems, error: checkError } = await supabase
        .from('order_items')
        .select('order_id')
        .eq('product_id', productId);

      if (checkError) {
        console.error('Error checking order items:', checkError);
      }

      const affectedOrderCount = new Set((orderItems || []).map((item) => item.order_id).filter(Boolean)).size;
      const confirmMessage = affectedOrderCount > 0
        ? (
          isArabic
            ? `هذا المنتج مرتبط بـ ${affectedOrderCount} طلب(طلبات). حذف المنتج سيحذف هذه الطلبات نهائيًا أيضًا. هل تريد المتابعة؟`
            : `This product is linked to ${affectedOrderCount} order(s). Deleting this product will permanently delete those orders as well. Do you want to continue?`
        )
        : (
          isArabic
            ? 'هل أنت متأكد من حذف هذا المنتج؟'
            : 'Are you sure you want to delete this product?'
        );

      if (!window.confirm(confirmMessage)) {
        return;
      }

      const { data, error } = await supabase.rpc('superadmin_delete_product_with_orders', {
        p_product_id: productId
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete product');
      }

      showToast(
        isArabic ? '✓ تم الحذف' : '✓ Product Deleted',
        isArabic
          ? `تم حذف المنتج بنجاح${data?.deleted_orders ? ` مع ${data.deleted_orders} طلب(طلبات) مرتبطة` : ''}`
          : `Product has been deleted successfully${data?.deleted_orders ? ` with ${data.deleted_orders} related order(s)` : ''}`,
        'success'
      );
      // Refresh products list
      setProducts(products.filter(p => p.id !== productId));
    } catch (err) {
      console.error('Error deleting product:', err);
      alert((isArabic ? 'فشل حذف المنتج: ' : 'Failed to delete product: ') + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate category is selected
      if (!formData.category_id) {
        alert('Please select a category first');
        setSubmitting(false);
        return;
      }

      // Validate subcategory is selected (mandatory)
      if (!formData.subcategory_id) {
        alert('Please select a subcategory (required)');
        setSubmitting(false);
        return;
      }

      // Validate subcategory belongs to selected category
      if (formData.subcategory_id && formData.category_id) {
        const selectedSubcategory = subcategories.find(sub => sub.id === formData.subcategory_id);
        if (selectedSubcategory && selectedSubcategory.category_id !== formData.category_id) {
          // Fetch category name for better error message
          const { data: categoryData } = await supabase
            .from('categories')
            .select('name_en, name_ar')
            .eq('id', formData.category_id)
            .single();
          
          const categoryName = categoryData?.name_en || categoryData?.name_ar || 'the selected category';
          const subcategoryName = selectedSubcategory.name_en || selectedSubcategory.name_ar || 'the selected subcategory';
          
          alert(
            `Error: The subcategory "${subcategoryName}" does not belong to the category "${categoryName}". Please select a valid subcategory or change the category.`
          );
          setSubmitting(false);
          return;
        }
      }

      const validStatus = (formData.status === 'active' || formData.status === 'inactive')
        ? formData.status
        : 'active';

      const cleanedImages = (formData.images || []).filter((img) => img && img.trim());
      if (cleanedImages.length === 0) {
        alert(
          isArabic
            ? 'يرجى إضافة صورة واحدة على الأقل للمنتج'
            : 'Please add at least one product image'
        );
        setSubmitting(false);
        return;
      }

      const productData = {
        name: formData.name,
        name_ar: formData.name_ar || null,
        sku: formData.sku,
        description: formData.description || null,
        img: formData.img?.trim() || null,
        images: cleanedImages,
        price: parseFloat(formData.price) || 0,
        currency: formData.currency,
        stock: parseInt(formData.stock) || 0,
        weight_kg: (() => {
          const parsedWeight = parseFloat(formData.weight_kg);
          if (!Number.isFinite(parsedWeight) || parsedWeight < 0) return 1.000;
          return parsedWeight;
        })(),
        status: validStatus,
        brand_id: formData.brand_id || null,
        category_id: formData.category_id || null,
        subcategory_id: formData.subcategory_id || null,
        attributes: formData.attributes,
        published_at: validStatus === 'active' ? new Date().toISOString() : null,
        dis: formData.dis || 'no',
      };

      let error;
      if (editingProduct) {
        // Update existing product
        const { error: updateError } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);
        error = updateError;
      } else {
        // Create new product
        productData.created_by = session?.user?.id || null;
        const { error: insertError } = await supabase
          .from('products')
          .insert([productData])
          .select();
        error = insertError;
      }

      if (error) throw error;

      // Clear draft from localStorage after successful submission
      if (!editingProduct) {
        try {
          localStorage.removeItem(DRAFT_PRODUCT_KEY);
          setHasDraft(false);
          console.log('✅ Draft cleared after successful submission');
        } catch (error) {
          console.error('Failed to clear draft:', error);
        }
      }

      showToast(
        editingProduct ? (isArabic ? '✓ تم التحديث' : '✓ Product Updated') : (isArabic ? '✓ تم الإضافة' : '✓ Product Added'),
        editingProduct ? (isArabic ? 'تم تحديث المنتج بنجاح' : 'Product has been updated successfully') : (isArabic ? 'تم إضافة المنتج بنجاح' : 'Product has been added successfully'),
        'success'
      );
      setIsModalOpen(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        name_ar: '',
        sku: '',
        description: '',
        images: [],
        price: 0,
        currency: 'OMR',
        stock: 0,
        weight_kg: 1.000,
        status: 'active',
        brand_id: '',
        category_id: '',
        subcategory_id: '',
        attributes: {},
        published_at: null,
        dis: 'no',
      });
      // setNewImageUrl('');
      // Refresh products list
      refreshProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      
      // Improve error message for subcategory/category mismatch
      let errorMessage = err.message || 'Unknown error occurred';
      
      if (errorMessage.includes('subcategory') && errorMessage.includes('does not belong to category')) {
        // Try to extract IDs and fetch names
        const subcategoryMatch = errorMessage.match(/subcategory \(([^)]+)\)/);
        const categoryMatch = errorMessage.match(/category \(([^)]+)\)/);
        
        if (categoryMatch && categoryMatch[1] !== '<NULL>') {
          try {
            const { data: categoryData } = await supabase
              .from('categories')
              .select('name_en, name_ar')
              .eq('id', categoryMatch[1])
              .single();
            
            const categoryName = categoryData?.name_en || categoryData?.name_ar || categoryMatch[1];
            
            if (subcategoryMatch && subcategoryMatch[1] !== '<NULL>') {
              const { data: subcategoryData } = await supabase
                .from('subcategories')
                .select('name_en, name_ar')
                .eq('id', subcategoryMatch[1])
                .single();
              
              const subcategoryName = subcategoryData?.name_en || subcategoryData?.name_ar || subcategoryMatch[1];
              
              errorMessage = `Error: The subcategory "${subcategoryName}" does not belong to the category "${categoryName}". Please select a valid subcategory or remove the subcategory selection.`;
            } else {
              errorMessage = `Error: Cannot save product with a subcategory but no main category. Please select a category or remove the subcategory selection.`;
            }
          } catch (fetchErr) {
            // If fetching names fails, use a generic improved message
            errorMessage = `Error: The selected subcategory does not belong to the selected category. Please check your selections.`;
          }
        } else {
          errorMessage = `Error: Cannot save product with a subcategory but no main category. Please select a category or remove the subcategory selection.`;
        }
      }
      
      alert(`Failed to save product: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = async () => {
    // Check if there's unsaved data (only for new products)
    // Note: beforeunload listener is automatically cleaned up by useEffect when isModalOpen changes
    if (!editingProduct) {
      const hasData = formData.name || formData.name_ar || formData.sku || 
                      formData.description || formData.img || 
                      formData.images.length > 0 || 
                      (formData.price > 0 && formData.price !== 0);
      
      if (hasData) {
        const confirmMessage = isArabic 
          ? 'بياناتك محفوظة تلقائياً! يمكنك العودة لاحقاً والمتابعة من حيث توقفت.'
          : 'Your data is auto-saved! You can come back later and continue where you left off.';
        
        const saveDraft = await showConfirm(
          isArabic ? '💾 إغلاق المسودة' : '💾 Close Draft',
          confirmMessage,
          '❓',
          'warning'
        );
        
        if (!saveDraft) {
          return;
        } else {
          console.log('💾 Draft kept in localStorage');
          showToast(
            isArabic ? '✓ تم الحفظ' : '✓ Saved',
            isArabic ? 'بياناتك محفوظة بأمان' : 'Your data is safely saved',
            'success'
          );
        }
      }
    }

    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      name_ar: '',
      sku: '',
      description: '',
      images: [],
      price: 0,
      currency: 'OMR',
      stock: 0,
      weight_kg: 1.000,
      status: 'active',
      brand_id: '',
      category_id: '',
      subcategory_id: '',
      attributes: {},
      published_at: null,
      dis: 'no',
    });
  };

  // Toggle boost status for a product
  const handleToggleBoost = async (productId, currentBoostStatus) => {
    try {
      const newBoostStatus = !currentBoostStatus;
      
      const { error } = await supabase
        .from('products')
        .update({ boost: newBoostStatus })
        .eq('id', productId);

      if (error) throw error;

      // Update local state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, boost: newBoostStatus } : p
      ));
      
      // Show feedback
      alert(newBoostStatus ? 'Product boosted! It will appear at the top.' : 'Product boost removed.');
    } catch (err) {
      console.error('Error toggling boost:', err);
      alert('Failed to update boost status: ' + err.message);
    }
  };

  // Variant Management Functions
  const handleManageVariants = async (product) => {
    setCurrentProduct(product);
    setIsVariantModalOpen(true);
    await fetchProductVariants(product.id);
  };

  const fetchProductVariants = async (productId) => {
    try {
      setLoadingVariants(true);

      // Fetch option groups with values
      const { data: groupsData, error: groupsError } = await supabase
        .from('product_option_groups')
        .select(`
          *,
          values:product_option_values(
            id,
            value,
            meta,
            position
          )
        `)
        .eq('product_id', productId)
        .order('position', { ascending: true });

      if (groupsError) throw groupsError;

      // Sort values within each group
      const groupsWithSortedValues = (groupsData || []).map(group => ({
        ...group,
        values: (group.values || []).sort((a, b) => (a.position || 0) - (b.position || 0))
      }));

      // Fetch variants with option mappings (including img column)
      const { data: variantsData, error: variantsError } = await supabase
        .from('product_variants')
        .select(`
          *,
          variant_options:product_variant_options(
            group_id,
            value_id,
            option_group:product_option_groups(
              id,
              name,
              label
            ),
            option_value:product_option_values(
              id,
              value
            )
          )
        `)
        .eq('product_id', productId)
        .order('created_at', { ascending: true });

      if (variantsError) throw variantsError;

      setOptionGroups(groupsWithSortedValues || []);
      setVariants((variantsData || []).map(v => ({
        ...v,
        price: Number(v.price || 0),
        stock: Number(v.stock || 0),
      })));
    } catch (err) {
      console.error('Error fetching variants:', err);
      alert('Failed to load variants: ' + err.message);
    } finally {
      setLoadingVariants(false);
    }
  };

  // Option Group Management
  const handleAddOptionGroup = () => {
    setEditingOptionGroup(null);
    setIsAddingOptionGroup(true);
    setOptionGroupForm({ name: '', label: '', position: optionGroups.length });
  };

  const handleEditOptionGroup = (group) => {
    setEditingOptionGroup(group);
    setIsAddingOptionGroup(false);
    setOptionGroupForm({
      name: group.name || '',
      label: group.label || '',
      position: group.position || 0
    });
  };

  const handleSaveOptionGroup = async () => {
    if (!currentProduct || !optionGroupForm.name.trim()) {
      alert(isArabic ? 'الرجاء إدخال اسم المتغير' : 'Please enter variant name');
      return;
    }

    try {
      const groupData = {
        product_id: currentProduct.id,
        name: optionGroupForm.name.trim(),
        label: optionGroupForm.label.trim() || optionGroupForm.name.trim(),
        position: parseInt(optionGroupForm.position) || 0
      };

      let error;
      if (editingOptionGroup) {
        const { error: updateError } = await supabase
          .from('product_option_groups')
          .update(groupData)
          .eq('id', editingOptionGroup.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('product_option_groups')
          .insert([groupData]);
        error = insertError;
      }

      if (error) throw error;

      alert(editingOptionGroup ? (isArabic ? 'تم تحديث الخيار!' : 'Option updated!') : (isArabic ? 'تم إضافة الخيار!' : 'Option added!'));
      setEditingOptionGroup(null);
      setIsAddingOptionGroup(false);
      setOptionGroupForm({ name: '', label: '', position: 0 });
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error saving option group:', err);
      alert(isArabic ? `فشل حفظ الخيار: ${err.message}` : `Failed to save option: ${err.message}`);
    }
  };

  const handleDeleteOptionGroup = async (groupId) => {
    if (!window.confirm(isArabic ? 'حذف هذا الخيار؟ سيتم حذف جميع قيمه وتعيينات المتغيرات أيضًا.' : 'Delete this option? This will also delete all its values and variant mappings.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('product_option_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;

      alert(isArabic ? 'تم حذف الخيار!' : 'Option deleted!');
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error deleting option group:', err);
      alert(isArabic ? `فشل حذف الخيار: ${err.message}` : `Failed to delete option: ${err.message}`);
    }
  };

  // Option Value Management
  const handleAddOptionValue = (group) => {
    setEditingOptionValue(null);
    setOptionValueForm({
      value: '',
      position: (group.values?.length || 0),
      group_id: group.id
    });
  };

  const handleEditOptionValue = (value, groupId) => {
    setEditingOptionValue(value);
    setOptionValueForm({
      value: value.value || '',
      position: value.position || 0,
      group_id: groupId
    });
  };

  const handleSaveOptionValue = async () => {
    if (!optionValueForm.value.trim() || !optionValueForm.group_id) {
      alert('Please enter option value');
      return;
    }

    try {
      const valueData = {
        group_id: optionValueForm.group_id,
        value: optionValueForm.value.trim(),
        position: parseInt(optionValueForm.position) || 0
      };

      let error;
      if (editingOptionValue) {
        const { error: updateError } = await supabase
          .from('product_option_values')
          .update(valueData)
          .eq('id', editingOptionValue.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('product_option_values')
          .insert([valueData]);
        error = insertError;
      }

      if (error) throw error;

      alert(editingOptionValue ? 'Option value updated!' : 'Option value added!');
      setEditingOptionValue(null);
      setOptionValueForm({ value: '', position: 0, group_id: '' });
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error saving option value:', err);
      alert('Failed to save option value: ' + err.message);
    }
  };

  const handleDeleteOptionValue = async (valueId) => {
    if (!window.confirm('Delete this option value? This will remove it from all variants.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('product_option_values')
        .delete()
        .eq('id', valueId);

      if (error) throw error;

      alert('Option value deleted!');
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error deleting option value:', err);
      alert('Failed to delete option value: ' + err.message);
    }
  };

  // Variant Image Upload
  const handleVariantImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVariantImage(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `variant_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      console.log('Uploading variant image to products bucket:', fileName);

      // Upload to Supabase Storage (using products bucket)
      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload image: ' + uploadError.message);
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('products')
        .getPublicUrl(fileName);

      console.log('Upload successful, public URL:', publicUrl);

      // Automatically set the img field with the uploaded URL
      setVariantForm(prev => ({
        ...prev,
        img: publicUrl
      }));

      // Reset file input
      if (variantImageInputRef.current) {
        variantImageInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Error uploading variant image:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingVariantImage(false);
    }
  };

  // Variant Management
  const handleAddVariant = () => {
    setEditingVariant(null);
    setIsAddingVariant(true);
    setVariantForm({
      sku: '',
      price: currentProduct?.price || 0,
      stock: 0,
      status: 'active',
      img: '',
      images: [],
      attributes: {},
      selectedOptions: {}
    });
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setIsAddingVariant(false);

    // Build selectedOptions from variant_options
    const selectedOptions = {};
    if (variant.variant_options) {
      variant.variant_options.forEach(vo => {
        selectedOptions[vo.group_id] = vo.value_id;
      });
    }

    setVariantForm({
      sku: variant.sku || '',
      price: variant.price || 0,
      stock: variant.stock || 0,
      status: variant.status || 'active',
      img: variant.img || '',
      images: variant.images || [],
      attributes: variant.attributes || {},
      selectedOptions
    });
  };

  const handleSaveVariant = async () => {
    if (!currentProduct) return;

    // Validate that all option groups have selected values
    if (optionGroups.length > 0) {
      const allSelected = optionGroups.every(group =>
        variantForm.selectedOptions[group.id] != null
      );
      if (!allSelected) {
        alert(isArabic ? `الرجاء اختيار خيار لـ: ${optionGroups.map(g => g.label || g.name).join(', ')}` : `Please select an option for: ${optionGroups.map(g => g.label || g.name).join(', ')}`);
        return;
      }
    }

    try {
      const variantData = {
        product_id: currentProduct.id,
        sku: variantForm.sku.trim() || null,
        price: parseFloat(variantForm.price) || 0,
        currency: currentProduct.currency || 'OMR',
        stock: parseInt(variantForm.stock) || 0,
        status: variantForm.status,
        img: variantForm.img.trim() || null,
        images: variantForm.images.filter(img => img.trim()),
        attributes: variantForm.attributes
      };

      let variantId;
      if (editingVariant) {
        const { data, error: updateError } = await supabase
          .from('product_variants')
          .update(variantData)
          .eq('id', editingVariant.id)
          .select()
          .single();
        if (updateError) throw updateError;
        variantId = data.id;
      } else {
        const { data, error: insertError } = await supabase
          .from('product_variants')
          .insert([variantData])
          .select()
          .single();
        if (insertError) throw insertError;
        variantId = data.id;
      }

      // Update variant option mappings
      if (optionGroups.length > 0) {
        // Delete existing mappings
        const { error: deleteError } = await supabase
          .from('product_variant_options')
          .delete()
          .eq('variant_id', variantId);

        if (deleteError) throw deleteError;

        // Insert new mappings
        const optionMappings = Object.entries(variantForm.selectedOptions).map(([groupId, valueId]) => ({
          variant_id: variantId,
          group_id: groupId,
          value_id: valueId
        }));

        if (optionMappings.length > 0) {
          const { error: insertMappingsError } = await supabase
            .from('product_variant_options')
            .insert(optionMappings);

          if (insertMappingsError) throw insertMappingsError;
        }
      }

      alert(editingVariant ? 'Variant updated!' : 'Variant added!');
      setEditingVariant(null);
      setIsAddingVariant(false);
      setVariantForm({
        sku: '',
        price: 0,
        stock: 0,
        status: 'active',
        img: '',
        images: [],
        attributes: {},
        selectedOptions: {}
      });
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error saving variant:', err);
      alert('Failed to save variant: ' + err.message);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Delete this variant?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('product_variants')
        .delete()
        .eq('id', variantId);

      if (error) throw error;

      alert('Variant deleted!');
      await fetchProductVariants(currentProduct.id);
    } catch (err) {
      console.error('Error deleting variant:', err);
      alert('Failed to delete variant: ' + err.message);
    }
  };

  const handleCloseVariantModal = () => {
    setIsVariantModalOpen(false);
    setCurrentProduct(null);
    setOptionGroups([]);
    setVariants([]);
    setEditingOptionGroup(null);
    setIsAddingOptionGroup(false);
    setEditingOptionValue(null);
    setEditingVariant(null);
    setIsAddingVariant(false);
    setOptionGroupForm({ name: '', label: '', position: 0 });
    setOptionValueForm({ value: '', position: 0, group_id: '' });
    setVariantForm({
      sku: '',
      price: 0,
      stock: 0,
      status: 'active',
      img: '',
      images: [],
      attributes: {},
      selectedOptions: {}
    });
  };

  // Filter products by selected brand and search term.
  const filteredProducts = useMemo(() => {
    const brandFilteredProducts = selectedBrandId
      ? products.filter((product) => String(product.brand_id || '') === String(selectedBrandId))
      : products;

    if (!searchTerm.trim()) {
      return brandFilteredProducts;
    }
    const term = searchTerm.toLowerCase();
    return brandFilteredProducts.filter(product => {
      const name = (product.name || '').toLowerCase();
      const nameAr = (product.name_ar || '').toLowerCase();
      const sku = (product.sku || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const categoryName = (product.category?.name_en || product.category?.name_ar || '').toLowerCase();
      const subcategoryName = (product.subcategory?.name_en || product.subcategory?.name_ar || '').toLowerCase();
      const brandName = (product.brand?.name || '').toLowerCase();
      const brandPhone = (product.brand?.phone || '').toLowerCase();
      const price = (product.price || 0).toString();
      const stock = (product.stock || 0).toString();
      const status = (product.status || '').toLowerCase();
      
      return name.includes(term) || nameAr.includes(term) || sku.includes(term) || 
             description.includes(term) || categoryName.includes(term) || 
             subcategoryName.includes(term) || brandName.includes(term) ||
             brandPhone.includes(term) || price.includes(term) || 
             stock.includes(term) || status.includes(term);
    });
  }, [products, searchTerm, selectedBrandId]);

  // Best sellers first: units sold (paid orders), then revenue, then newest
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    const unitsFor = (p) => salesStats[String(p.id)]?.units ?? 0;
    const revFor = (p) => salesStats[String(p.id)]?.revenue ?? 0;
    list.sort((a, b) => {
      const du = unitsFor(b) - unitsFor(a);
      if (du !== 0) return du;
      const dr = revFor(b) - revFor(a);
      if (dr !== 0) return dr;
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return list;
  }, [filteredProducts, salesStats]);

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [searchTerm, selectedBrandId]);

  // Slice to show only displayedCount items (order preserved = sales rank)
  const displayedProducts = sortedProducts.slice(0, displayedCount);
  const hasMoreProducts = sortedProducts.length > displayedCount;

  return (
    <>
      <ProductsContainer>
        <Header>
          <Title>Product List</Title>
          <ActionButtons>
            <Button variant="primary" onClick={async () => {
              setEditingProduct(null);
              
              // Try to load draft from localStorage
              try {
                const savedDraft = localStorage.getItem(DRAFT_PRODUCT_KEY);
                if (savedDraft) {
                  const draftData = JSON.parse(savedDraft);
                  const savedAt = draftData.savedAt;
                  delete draftData.savedAt;
                  
                  const timeAgo = new Date(savedAt).toLocaleString(isArabic ? 'ar-SA' : 'en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  const confirmMessage = isArabic
                    ? `تم حفظ بياناتك تلقائياً في ${timeAgo}. يمكنك المتابعة من حيث توقفت!`
                    : `Your data was auto-saved on ${timeAgo}. You can continue where you left off!`;
                  
                  const restoreDraft = await showConfirm(
                    isArabic ? '📝 استعادة المسودة' : '📝 Restore Draft',
                    confirmMessage,
                    '💾',
                    'info',
                    isArabic ? 'بدء جديد' : 'Start New',
                    isArabic ? 'استعادة' : 'Restore'
                  );
                  
                  if (restoreDraft) {
                    setFormData(draftData);
                    showToast(
                      isArabic ? '✓ تم الاستعادة' : '✓ Draft Restored',
                      isArabic ? 'تم استعادة بياناتك بنجاح' : 'Your data has been successfully restored',
                      'success'
                    );
                    console.log('♻️ Draft restored from localStorage');
                  } else {
                    localStorage.removeItem(DRAFT_PRODUCT_KEY);
                    setHasDraft(false);
                    setFormData({
                      name: '',
                      name_ar: '',
                      sku: '',
                      description: '',
                      images: [],
                      price: 0,
                      currency: 'OMR',
                      stock: 0,
                      weight_kg: 1.000,
                      status: 'active',
                      brand_id: '',
                      category_id: '',
                      subcategory_id: '',
                      attributes: {},
                      published_at: null,
                      dis: 'no',
                    });
                    console.log('🗑️ Draft discarded, starting fresh');
                  }
                } else {
                  setFormData({
                    name: '',
                    name_ar: '',
                    sku: '',
                    description: '',
                    images: [],
                    price: 0,
                    currency: 'OMR',
                    stock: 0,
                    weight_kg: 1.000,
                    status: 'active',
                    brand_id: '',
                    category_id: '',
                    subcategory_id: '',
                    attributes: {},
                    published_at: null,
                    dis: 'no',
                  });
                }
              } catch (error) {
                console.error('Failed to load draft:', error);
                setFormData({
                  name: '',
                  name_ar: '',
                  sku: '',
                  description: '',
                  images: [],
                  price: 0,
                  currency: 'OMR',
                  stock: 0,
                  weight_kg: 1.000,
                  status: 'active',
                  brand_id: '',
                  category_id: '',
                  subcategory_id: '',
                  attributes: {},
                  published_at: null,
                  dis: 'no',
                });
              }
              
              setIsModalOpen(true);
            }}>
              <FiPlus /> {isArabic ? 'إضافة منتج' : 'Add Product'}
            </Button>
          </ActionButtons>
        </Header>

        <FiltersRow>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder={isArabic ? 'ابحث بالاسم أو SKU أو الفئة أو الماركة أو السعر أو المخزون أو الحالة...' : 'Search by name, SKU, category, brand, price, stock, or status...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')} title={isArabic ? 'مسح البحث' : 'Clear search'}>
                <FiX size={18} />
              </ClearButton>
            )}
          </SearchContainer>
          <BrandFilterSelect
            value={selectedBrandId}
            onChange={(e) => setSelectedBrandId(e.target.value)}
            aria-label="Filter by brand"
            title="Filter by brand"
          >
            <option value="">{isArabic ? 'كل الماركات' : 'All Brands'}</option>
            {(brands || []).map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </BrandFilterSelect>
        </FiltersRow>

        {loading ? (
          <p>{isArabic ? 'جارٍ تحميل المنتجات...' : 'Loading products...'}</p>
        ) : products.length === 0 ? (
          <TableWrapper>
            <EmptyState>
              <EmptyTitle>{isArabic ? 'لا توجد منتجات بعد' : 'No Products Yet'}</EmptyTitle>
              <EmptyText>{isArabic ? 'ابدأ بإضافة أول منتج' : 'Get started by adding your first product'}</EmptyText>
            </EmptyState>
          </TableWrapper>
        ) : (
          <TableWrapper>
            <SortHintBar>
              <FiTrendingUp size={18} />
              <span>
                {isArabic
                  ? <><strong>الأكثر مبيعًا أولاً</strong> — مرتبة حسب الكمية المباعة (طلبات مدفوعة) ثم الإيراد. أعلى 3 صفوف مميزة.</>
                  : <><strong>Best sellers first</strong> — sorted by units sold (paid orders), then revenue. Top 3 rows are highlighted.</>}
              </span>
            </SortHintBar>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell style={{ width: 56 }}>#</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الصورة' : 'Image'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الاسم' : 'Name'}</TableHeaderCell>
                  <TableHeaderCell>SKU</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الفئة' : 'Category'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الفئة الفرعية' : 'Subcategory'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'السعر' : 'Price'}</TableHeaderCell>
                  <TableHeaderCell title={isArabic ? 'الأعلى مبيعًا يظهر أولاً' : 'Higher sellers appear first'}>{isArabic ? 'المباع' : 'Units sold'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإيراد' : 'Revenue'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'التقييمات' : 'Reviews'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'المخزون' : 'Stock'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'التعزيز' : 'Boost'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {displayedProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="14" style={{ textAlign: 'center', padding: '40px' }}>
                      <EmptyTitle>{searchTerm || selectedBrandId ? (isArabic ? 'لا توجد منتجات مطابقة للفلاتر' : 'No products found matching your filters') : (isArabic ? 'لا توجد منتجات متاحة' : 'No products available')}</EmptyTitle>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedProducts.map((product, index) => {
                    const rank = index + 1;
                    const tier = rank <= 3 ? rank : 0;
                    return (
                  <TableRow key={product.id} $boosted={product.boost} $rank={tier}>
                    <TableCell style={{ width: 56 }}>
                      <RankBadge $tier={tier}>{rank}</RankBadge>
                    </TableCell>
                    <TableCell>
                      {product.images && product.images.length > 0 && product.images[0] ? (
                        <ProductImage src={product.images[0]} alt={product.name} />
                      ) : (
                        <ImagePlaceholder>{isArabic ? 'لا صورة' : 'No Image'}</ImagePlaceholder>
                      )}
                    </TableCell>
                    <TableCell>
                      <strong>{product.name || 'N/A'}</strong>
                      {product.description && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          {product.description.substring(0, 50)}...
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{product.sku || 'N/A'}</TableCell>
                    <TableCell>
                      {product.category ? (product.category.name_en || product.category.name_ar || 'N/A') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {product.subcategory ? (product.subcategory.name_en || product.subcategory.name_ar || 'N/A') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {product.currency} {product.price?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>
                      {salesStatsLoading ? (
                        <span style={{ color: '#9ca3af' }}>…</span>
                      ) : (
                        <UnitsPill>
                          {(salesStats[String(product.id)]?.units ?? 0).toLocaleString()}
                        </UnitsPill>
                      )}
                    </TableCell>
                    <TableCell>
                      {salesStatsLoading ? (
                        <span style={{ color: '#9ca3af' }}>…</span>
                      ) : (
                        <RevenueText>
                          {product.currency || 'OMR'}{' '}
                          {(salesStats[String(product.id)]?.revenue ?? 0).toFixed(2)}
                        </RevenueText>
                      )}
                    </TableCell>
                    <TableCell>
                      {reviewsStatsLoading ? (
                        <span style={{ color: '#9ca3af' }}>…</span>
                      ) : (
                        <ReviewsPill>
                          {(reviewsStats[String(product.id)]?.count ?? 0).toLocaleString()}
                        </ReviewsPill>
                      )}
                    </TableCell>
                    <TableCell>{product.stock !== null ? product.stock : 'N/A'}</TableCell>
                    <TableCell>
                      <BoostToggle
                        $boosted={product.boost}
                        onClick={() => handleToggleBoost(product.id, product.boost)}
                        title={product.boost ? 'Click to remove boost' : 'Click to boost this product'}
                      >
                        <FiZap />
                      </BoostToggle>
                    </TableCell>
                    <TableCell>
                      {product.created_at
                        ? new Date(product.created_at).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <ActionButtonsCell>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEdit(product)}>
                          <FiEdit />
                        </Button>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleManageVariants(product)}>
                          Variants
                        </Button>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleDelete(product.id)}>
                          <FiTrash2 />
                        </Button>
                      </ActionButtonsCell>
                    </TableCell>
                  </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        )}

        {!loading && hasMoreProducts && (
          <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
            {isArabic ? `تحميل المزيد (${sortedProducts.length - displayedCount} متبقي)` : `Load More (${sortedProducts.length - displayedCount} remaining)`}
          </LoadMoreButton>
        )}
      </ProductsContainer>

      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingProduct ? (isArabic ? 'تعديل المنتج' : 'Edit Product') : (isArabic ? 'إضافة منتج جديد' : 'Add New Product')}</ModalTitle>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!editingProduct && hasDraft && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={async () => {
                      const confirmCancel = await showConfirm(
                        '🗑️ Delete Draft',
                        'Are you sure you want to delete the draft? All saved data will be deleted.',
                        '⚠️',
                        'warning'
                      );
                      
                      if (confirmCancel) {
                        try {
                          localStorage.removeItem(DRAFT_PRODUCT_KEY);
                          setHasDraft(false);
                          setFormData({
                            name: '',
                            name_ar: '',
                            sku: '',
                            description: '',
                            images: [],
                            price: 0,
                            currency: 'OMR',
                            stock: 0,
                            weight_kg: 1.000,
                            status: 'active',
                            brand_id: '',
                            category_id: '',
                            subcategory_id: '',
                            attributes: {},
                            published_at: null,
                            dis: 'no',
                          });
                          setIsModalOpen(false);
                          showToast(
                            '✓ Draft Deleted',
                            'Draft has been deleted successfully',
                            'success'
                          );
                          console.log('🗑️ Draft deleted and cleared');
                        } catch (error) {
                          console.error('Failed to delete draft:', error);
                        }
                      }
                    }}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    🗑️ Delete Draft
                  </Button>
                )}
                <CloseButton onClick={handleCloseModal}>
                  <FiX />
                </CloseButton>
              </div>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label>Product Name (English) *</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter product name in English"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Product Name (Arabic)</Label>
                  <Input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleInputChange}
                    placeholder="Enter product name in Arabic"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>SKU *</Label>
                  <Input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter SKU"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter product description"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Currency</Label>
                  <Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    <option value="OMR">OMR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Stock *</Label>
                  <Input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    min="0"
                    placeholder="0"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number"
                    name="weight_kg"
                    value={formData.weight_kg === null || formData.weight_kg === undefined ? '' : formData.weight_kg}
                    onChange={handleInputChange}
                    min="0"
                    step="0.001"
                    placeholder="1.000"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                    Product weight for shipping calculation
                  </small>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </FormGroup>
                <FormGroup></FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Discount</Label>
                  <Select
                    name="dis"
                    value={formData.dis}
                    onChange={handleInputChange}
                  >
                    <option value="no">No Discount</option>
                    <option value="5">5%</option>
                    <option value="10">10%</option>
                    <option value="15">15%</option>
                    <option value="20">20%</option>
                    <option value="25">25%</option>
                    <option value="30">30%</option>
                    <option value="35">35%</option>
                    <option value="40">40%</option>
                    <option value="45">45%</option>
                    <option value="50">50%</option>
                    <option value="55">55%</option>
                    <option value="60">60%</option>
                    <option value="65">65%</option>
                    <option value="70">70%</option>
                    <option value="75">75%</option>
                    <option value="80">80%</option>
                    <option value="85">85%</option>
                    <option value="90">90%</option>
                    <option value="95">95%</option>
                  </Select>
                </FormGroup>
                <FormGroup></FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Brand</Label>
                  <Select
                    name="brand_id"
                    value={formData.brand_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a brand</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name_en || category.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </FormRow>

              {formData.category_id && (
                <FormRow>
                  <FormGroup>
                    <Label>Subcategory <span style={{ color: 'red' }}>*</span></Label>
                    <Select
                      name="subcategory_id"
                      value={formData.subcategory_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">
                        {subcategories.length === 0
                          ? 'No subcategories available'
                          : 'Select a subcategory'}
                      </option>
                      {subcategories.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name_en || subcategory.name_ar || subcategory.id}
                        </option>
                      ))}
                    </Select>
                    {formData.category_id && subcategories.length === 0 && (
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                        No subcategories found for this category. Product can be saved without a subcategory.
                      </small>
                    )}
                  </FormGroup>
                  <FormGroup></FormGroup>
                </FormRow>
              )}

              <FormGroup>
                <Label>Main Product Image (img column)</Label>
                <Input
                  type="text"
                  name="img"
                  value={formData.img || ''}
                  onChange={handleInputChange}
                  placeholder="Enter main image URL"
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingAdditionalImages}
                  />
                  <AddImageButton
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAdditionalImages}
                    style={{ background: uploadingAdditionalImages ? '#d1fae5' : '#f0fdf4' }}
                  >
                    {uploadingAdditionalImages ? 'Uploading...' : '✂️ Crop & Upload Main Image'}
                  </AddImageButton>
                  {formData.img && (
                    <div style={{ flex: 1 }}>
                      <ImagePreviewImg
                        src={formData.img}
                        alt="Main product image"
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Product Images</Label>
                {/* <ImageUrlInput>
                  <Input
                    type="text"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Enter image URL"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImage())}
                  />
                  <AddImageButton type="button" onClick={handleAddImage}>
                    Add Image
                  </AddImageButton>
                </ImageUrlInput> */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    ref={additionalImagesFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAddImage}
                    style={{ display: 'none' }}
                    disabled={uploadingAdditionalImages}
                  />
                  <AddImageButton
                    type="button"
                    onClick={() => additionalImagesFileInputRef.current?.click()}
                    disabled={uploadingAdditionalImages}
                    style={{ background: uploadingAdditionalImages ? '#d1fae5' : '#f0fdf4' }}
                  >
                    {uploadingAdditionalImages
                      ? ('Uploading...')
                      : ('📤 Upload & Crop Image')}
                  </AddImageButton>
                  <small style={{ color: '#6b7280', fontSize: '12px', alignSelf: 'center' }}>
                    {'Image will be cropped to 1200×1200 before upload'}
                  </small>
                </div>
                {formData.images.length > 0 && (
                  <ImageList>
                    {formData.images.map((imageUrl, index) => (
                      <ImagePreview key={index}>
                        <ImagePreviewImg src={imageUrl} alt={`Preview ${index + 1}`} onError={(e) => e.target.style.display = 'none'} />
                        <RemoveImageButton type="button" onClick={() => handleRemoveImage(index)}>
                          Remove
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImageList>
                )}
              </FormGroup>

              <ModalActions>
                <CancelButton type="button" onClick={handleCloseModal}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : editingProduct ? 'Update Product' : 'Add Product'}
                </SaveButton>
              </ModalActions>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Variant Management Modal */}
      {isVariantModalOpen && currentProduct && (
        <ModalOverlay onClick={handleCloseVariantModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '95vh' }}>
            <ModalHeader>
              <ModalTitle>Manage Product Values - {currentProduct.name}</ModalTitle>
              <CloseButton onClick={handleCloseVariantModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            {loadingVariants ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>Loading variants...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {/* Option Groups Section */}
                <Section>
                  <SectionHeader>
                    <SectionTitle>{isArabic ? 'المتغيرات (مثل: الحجم، اللون)' : 'Variants (e.g., Size, Color)'}</SectionTitle>
                    <Button variant="primary" onClick={handleAddOptionGroup}>
                      <FiPlus /> {isArabic ? 'إضافة متغير' : 'Add Variant'}
                    </Button>
                  </SectionHeader>

                  {(editingOptionGroup !== null || isAddingOptionGroup) ? (
                    <Card style={{ marginBottom: '16px', padding: '16px' }}>
                      <FormGroup style={{ marginBottom: '12px' }}>
                        <Label>{isArabic ? 'اسم المتغير *' : 'Variant Name *'}</Label>
                        <Input
                          type="text"
                          value={optionGroupForm.name}
                          onChange={(e) => setOptionGroupForm({ ...optionGroupForm, name: e.target.value, label: e.target.value })}
                          placeholder={isArabic ? 'مثل: الحجم، اللون، المادة' : 'e.g., Size, Color, Material'}
                        />
                        <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {isArabic ? 'سيتم استخدام هذا الاسم للعرض والقيم' : 'This name will be used for both display and values'}
                        </small>
                      </FormGroup>
                      <FormGroup style={{ marginBottom: '12px' }}>
                        <Label>{isArabic ? 'الموضع' : 'Position'}</Label>
                        <Input
                          type="number"
                          value={optionGroupForm.position}
                          onChange={(e) => setOptionGroupForm({ ...optionGroupForm, position: parseInt(e.target.value) || 0 })}
                        />
                      </FormGroup>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button variant="primary" onClick={handleSaveOptionGroup}>
                          {editingOptionGroup ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'إضافة' : 'Add')}
                        </Button>
                        <Button variant="secondary" onClick={() => {
                          setEditingOptionGroup(null);
                          setIsAddingOptionGroup(false);
                          setOptionGroupForm({ name: '', label: '', position: 0 });
                        }}>
                          {isArabic ? 'إلغاء' : 'Cancel'}
                        </Button>
                      </div>
                    </Card>
                  ) : null}

                  {optionGroups.length === 0 ? (
                    <EmptyState>
                      <EmptyText>{isArabic ? 'لا توجد خيارات. أضف واحدًا لبدء إنشاء المتغيرات.' : 'No options. Add one to start creating variants.'}</EmptyText>
                    </EmptyState>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {optionGroups.map((group) => (
                        <Card key={group.id} style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div>
                              <strong>{group.label || group.name}</strong>
                              <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>({group.values?.length || 0} values)</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEditOptionGroup(group)}>
                                <FiEdit />
                              </Button>
                              <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleAddOptionValue(group)}>
                                <FiPlus /> Add Value
                              </Button>
                              <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleDeleteOptionGroup(group.id)}>
                                <FiTrash2 />
                              </Button>
                            </div>
                          </div>

                          {/* Option Values */}
                          {editingOptionValue && editingOptionValue.group_id === group.id ? (
                            <Card style={{ marginBottom: '12px', padding: '12px', background: '#f9fafb' }}>
                              <FormGroup style={{ marginBottom: '8px' }}>
                                <Label>Value</Label>
                                <Input
                                  type="text"
                                  value={optionValueForm.value}
                                  onChange={(e) => setOptionValueForm({ ...optionValueForm, value: e.target.value })}
                                  placeholder="e.g., Small, Red, Cotton"
                                />
                              </FormGroup>
                              <FormGroup style={{ marginBottom: '8px' }}>
                                <Label>Position</Label>
                                <Input
                                  type="number"
                                  value={optionValueForm.position}
                                  onChange={(e) => setOptionValueForm({ ...optionValueForm, position: parseInt(e.target.value) || 0 })}
                                />
                              </FormGroup>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Button variant="primary" style={{ padding: '6px 12px' }} onClick={handleSaveOptionValue}>
                                  {editingOptionValue ? 'Update' : 'Add'} Value
                                </Button>
                                <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => {
                                  setEditingOptionValue(null);
                                  setOptionValueForm({ value: '', position: 0, group_id: '' });
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </Card>
                          ) : !editingOptionValue && optionValueForm.group_id === group.id ? (
                            <Card style={{ marginBottom: '12px', padding: '12px', background: '#f9fafb' }}>
                              <FormGroup style={{ marginBottom: '8px' }}>
                                <Label>Value</Label>
                                <Input
                                  type="text"
                                  value={optionValueForm.value}
                                  onChange={(e) => setOptionValueForm({ ...optionValueForm, value: e.target.value })}
                                  placeholder="e.g., Small, Red, Cotton"
                                />
                              </FormGroup>
                              <FormGroup style={{ marginBottom: '8px' }}>
                                <Label>Position</Label>
                                <Input
                                  type="number"
                                  value={optionValueForm.position}
                                  onChange={(e) => setOptionValueForm({ ...optionValueForm, position: parseInt(e.target.value) || 0 })}
                                />
                              </FormGroup>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Button variant="primary" style={{ padding: '6px 12px' }} onClick={handleSaveOptionValue}>
                                  Add Value
                                </Button>
                                <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => {
                                  setEditingOptionValue(null);
                                  setOptionValueForm({ value: '', position: 0, group_id: '' });
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </Card>
                          ) : null}

                          {group.values && group.values.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                              {group.values.map((value) => (
                                <div key={value.id} style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 12px',
                                  background: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px'
                                }}>
                                  <span>{value.value}</span>
                                  <Button variant="secondary" style={{ padding: '4px 8px', minWidth: 'auto' }} onClick={() => handleEditOptionValue(value, group.id)}>
                                    <FiEdit />
                                  </Button>
                                  <Button variant="secondary" style={{ padding: '4px 8px', minWidth: 'auto' }} onClick={() => handleDeleteOptionValue(value.id)}>
                                    <FiTrash2 />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <EmptyText style={{ marginTop: '8px', fontSize: '12px' }}>No values yet</EmptyText>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </Section>

                {/* Variants Section */}
                <Section>
                  <SectionHeader>
                    <SectionTitle>Product Variants</SectionTitle>
                    <Button variant="primary" onClick={handleAddVariant} disabled={optionGroups.length === 0}>
                      <FiPlus /> Add Variant
                    </Button>
                  </SectionHeader>

                  {(editingVariant !== null || isAddingVariant) ? (
                    <Card style={{ marginBottom: '16px', padding: '16px' }}>
                      <FormRow>
                        <FormGroup>
                          <Label>SKU</Label>
                          <Input
                            type="text"
                            value={variantForm.sku}
                            onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })}
                            placeholder="Variant SKU"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Price *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={variantForm.price}
                            onChange={(e) => setVariantForm({ ...variantForm, price: parseFloat(e.target.value) || 0 })}
                            required
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Stock *</Label>
                          <Input
                            type="number"
                            value={variantForm.stock}
                            onChange={(e) => setVariantForm({ ...variantForm, stock: parseInt(e.target.value) || 0 })}
                            required
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Status</Label>
                          <Select
                            value={variantForm.status}
                            onChange={(e) => setVariantForm({ ...variantForm, status: e.target.value })}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Select>
                        </FormGroup>
                      </FormRow>

                      <FormGroup>
                        <Label>Variant Image</Label>
                        <input
                          ref={variantImageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleVariantImageUpload}
                          disabled={uploadingVariantImage}
                          style={{ display: 'none' }}
                        />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <AddImageButton
                            type="button"
                            onClick={() => variantImageInputRef.current?.click()}
                            disabled={uploadingVariantImage}
                            style={{ background: uploadingVariantImage ? '#d1fae5' : '#f0fdf4' }}
                          >
                            {uploadingVariantImage ? 'Uploading...' : '📤 Upload Image'}
                          </AddImageButton>
                          {variantForm.img && (
                            <ImagePreviewImg
                              src={variantForm.img}
                              alt="Variant preview"
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                            />
                          )}
                        </div>
                        {variantForm.img && (
                          <Input
                            type="text"
                            value={variantForm.img}
                            onChange={(e) => setVariantForm({ ...variantForm, img: e.target.value })}
                            placeholder="Variant image URL (auto-filled after upload)"
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </FormGroup>

                      {optionGroups.length > 0 && (
                        <FormGroup>
                          <Label>Select Options *</Label>
                          {optionGroups.map((group) => (
                            <FormGroup key={group.id} style={{ marginBottom: '12px' }}>
                              <Label style={{ fontSize: '14px', fontWeight: 600 }}>{group.label || group.name}</Label>
                              <Select
                                value={variantForm.selectedOptions[group.id] || ''}
                                onChange={(e) => setVariantForm({
                                  ...variantForm,
                                  selectedOptions: {
                                    ...variantForm.selectedOptions,
                                    [group.id]: e.target.value
                                  }
                                })}
                                required
                              >
                                <option value="">Select {group.label || group.name}</option>
                                {group.values?.map(value => (
                                  <option key={value.id} value={value.id}>{value.value}</option>
                                ))}
                              </Select>
                            </FormGroup>
                          ))}
                        </FormGroup>
                      )}

                      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                        <Button variant="primary" onClick={handleSaveVariant}>
                          {editingVariant ? 'Update' : 'Add'} Variant
                        </Button>
                        <Button variant="secondary" onClick={() => {
                          setEditingVariant(null);
                          setIsAddingVariant(false);
                          setVariantForm({
                            sku: '',
                            price: currentProduct?.price || 0,
                            stock: 0,
                            status: 'active',
                            img: '',
                            images: [],
                            attributes: {},
                            selectedOptions: {}
                          });
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </Card>
                  ) : null}

                  {variants.length === 0 ? (
                    <EmptyState>
                      <EmptyText>{isArabic ? 'لا توجد متغيرات. أضف الخيارات أولاً، ثم أنشئ المتغيرات.' : 'No variants. Add options first, then create variants.'}</EmptyText>
                    </EmptyState>
                  ) : (
                    <TableWrapper>
                      <Table>
                        <TableHeader>
                          <TableHeaderRow>
                            <TableHeaderCell>Image</TableHeaderCell>
                            <TableHeaderCell>Options</TableHeaderCell>
                            <TableHeaderCell>SKU</TableHeaderCell>
                            <TableHeaderCell>Price</TableHeaderCell>
                            <TableHeaderCell>Stock</TableHeaderCell>
                            <TableHeaderCell>Status</TableHeaderCell>
                            <TableHeaderCell>Actions</TableHeaderCell>
                          </TableHeaderRow>
                        </TableHeader>
                        <TableBody>
                          {variants.map((variant) => (
                            <TableRow key={variant.id}>
                              <TableCell>
                                {variant.img ? (
                                  <ProductImage src={variant.img} alt="Variant" />
                                ) : (
                                  <ImagePlaceholder>No Image</ImagePlaceholder>
                                )}
                              </TableCell>
                              <TableCell>
                                {variant.variant_options && variant.variant_options.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {variant.variant_options.map((vo, idx) => (
                                      <span key={idx} style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        background: '#f3f4f6',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                      }}>
                                        {vo.option_group?.label || vo.option_group?.name}: {vo.option_value?.value}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>No options</span>
                                )}
                              </TableCell>
                              <TableCell>{variant.sku || 'N/A'}</TableCell>
                              <TableCell>{currentProduct?.currency || 'OMR'} {variant.price?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell>{variant.stock || 0}</TableCell>
                              <TableCell>
                                <StatusBadge status={variant.status}>{variant.status}</StatusBadge>
                              </TableCell>
                              <TableCell>
                                <ActionButtonsCell>
                                  <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEditVariant(variant)}>
                                    <FiEdit />
                                  </Button>
                                  <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleDeleteVariant(variant.id)}>
                                    <FiTrash2 />
                                  </Button>
                                </ActionButtonsCell>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableWrapper>
                  )}
                </Section>
              </div>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}

      {showCropModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '720px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Crop Product Image</h3>
            <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '13px' }}>
              Target size: {PRODUCT_IMAGE_WIDTH} × {PRODUCT_IMAGE_HEIGHT}
            </p>
            <div style={{ position: 'relative', width: '100%', height: '420px', background: '#111827', borderRadius: '8px' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={PRODUCT_IMAGE_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280', width: '42px', textAlign: 'right' }}>{zoom.toFixed(1)}x</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  if (uploadingAdditionalImages) return;
                  setShowCropModal(false);
                  setImageSrc(null);
                  setCroppedAreaPixels(null);
                }}
                disabled={uploadingAdditionalImages}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  cursor: uploadingAdditionalImages ? 'not-allowed' : 'pointer',
                  opacity: uploadingAdditionalImages ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropUpload}
                disabled={uploadingAdditionalImages}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: uploadingAdditionalImages ? '#bbf7d0' : '#5efba9',
                  cursor: uploadingAdditionalImages ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  color: '#1f2937',
                  minWidth: '140px'
                }}
              >
                {uploadingAdditionalImages ? 'Uploading...' : 'Crop & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map(toast => (
            <Toast key={toast.id} type={toast.type}>
              <ToastIcon>
                {toast.type === 'success' && '✓'}
                {toast.type === 'error' && '✕'}
                {toast.type === 'warning' && '⚠'}
                {toast.type === 'info' && 'ℹ'}
              </ToastIcon>
              <ToastContent>
                <ToastTitle>{toast.title}</ToastTitle>
                <ToastMessage>{toast.message}</ToastMessage>
              </ToastContent>
              <ToastClose onClick={() => removeToast(toast.id)}>×</ToastClose>
            </Toast>
          ))}
        </ToastContainer>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <>
          <ConfirmOverlay onClick={confirmDialog.onCancel} />
          <ConfirmDialog>
            <ConfirmIcon type={confirmDialog.type}>{confirmDialog.icon}</ConfirmIcon>
            <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
            <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
            <ConfirmActions>
              <ConfirmButton onClick={confirmDialog.onCancel}>
                {confirmDialog.cancelLabel || (isArabic ? 'إلغاء' : 'Cancel')}
              </ConfirmButton>
              <ConfirmButton primary onClick={confirmDialog.onConfirm}>
                {confirmDialog.confirmLabel || (isArabic ? 'تأكيد' : 'Confirm')}
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmDialog>
        </>
      )}

    </>
  );
}
